// Production-ready storage solution - NO native modules
// Works on all platforms without any dependencies

interface StorageData {
  [key: string]: string;
}

class ProductionStorage {
  private static data: StorageData = {};
  private static initialized = false;
  private static readonly PREFIX = 'bookapt_';
  private static readonly GLOBAL_KEY = 'bookapt_global_storage';

  // Initialize storage
  private static async initialize(): Promise<void> {
    if (this.initialized) return;
    
    try {
      // Strategy 1: Try localStorage (web)
      if (typeof window !== 'undefined' && window.localStorage) {
        await this.loadFromLocalStorage();
        console.log('ProductionStorage: Initialized with localStorage');
        this.initialized = true;
        return;
      }
      
      // Strategy 2: Global variable persistence (React Native)
      await this.loadFromGlobal();
      console.log('ProductionStorage: Initialized with global storage');
      this.initialized = true;
      
    } catch (error) {
      console.error('ProductionStorage: Error initializing:', error);
      this.initialized = true; // Continue with empty storage
    }
  }

  // Load from localStorage (web)
  private static async loadFromLocalStorage(): Promise<void> {
    const keys = Object.keys(localStorage);
    const bookaptKeys = keys.filter(key => key.startsWith(this.PREFIX));
    
    bookaptKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        this.data[key] = value;
      }
    });
  }

  // Load from global variable
  private static async loadFromGlobal(): Promise<void> {
    try {
      // Try multiple global variable strategies
      if (typeof global !== 'undefined' && (global as any)[this.GLOBAL_KEY]) {
        this.data = { ...(global as any)[this.GLOBAL_KEY] };
      } else if (typeof window !== 'undefined' && (window as any)[this.GLOBAL_KEY]) {
        this.data = { ...(window as any)[this.GLOBAL_KEY] };
      } else if (typeof globalThis !== 'undefined' && (globalThis as any)[this.GLOBAL_KEY]) {
        this.data = { ...(globalThis as any)[this.GLOBAL_KEY] };
      }
    } catch (error) {
      console.warn('ProductionStorage: Global load failed:', error);
    }
  }

  // Set item
  static async setItem(key: string, value: string): Promise<void> {
    await this.initialize();
    
    const fullKey = `${this.PREFIX}${key}`;
    
    try {
      // Always store in memory first
      this.data[fullKey] = value;
      
      // Strategy 1: Try localStorage (web)
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.setItem(fullKey, value);
          console.log(`ProductionStorage: Stored ${fullKey} to localStorage`);
          return;
        } catch (error) {
          console.warn('ProductionStorage: localStorage failed:', error);
        }
      }
      
      // Strategy 2: Global variable persistence
      this.persistToGlobal();
      console.log(`ProductionStorage: Stored ${fullKey} to global storage`);
      
    } catch (error) {
      console.error(`ProductionStorage: Error storing ${fullKey}:`, error);
      // Don't throw - always succeed with memory storage
    }
  }

  // Get item
  static async getItem(key: string): Promise<string | null> {
    try {
      await this.initialize();
      
      const fullKey = `${this.PREFIX}${key}`;
      
      try {
        // First try memory
        let value = this.data[fullKey];
        if (value) {
          console.log(`ProductionStorage: Retrieved ${fullKey} from memory`);
          return value;
        }
        
        // Strategy 1: Try localStorage (web)
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            value = localStorage.getItem(fullKey);
            if (value) {
              this.data[fullKey] = value; // Cache in memory
              console.log(`ProductionStorage: Retrieved ${fullKey} from localStorage`);
              return value;
            }
          } catch (error) {
            console.warn('ProductionStorage: localStorage get failed:', error);
          }
        }
        
        // Strategy 2: Try global variable
        value = this.getFromGlobal(fullKey);
        if (value) {
          this.data[fullKey] = value; // Cache in memory
          console.log(`ProductionStorage: Retrieved ${fullKey} from global storage`);
          return value;
        }
        
        console.log(`ProductionStorage: ${fullKey} not found`);
        return null;
        
      } catch (error) {
        console.error(`ProductionStorage: Error retrieving ${fullKey}:`, error);
        return null;
      }
    } catch (error) {
      console.error(`ProductionStorage: Error in getItem for ${key}:`, error);
      return null;
    }
  }

  // Get from global variable
  private static getFromGlobal(key: string): string | null {
    try {
      console.log('ProductionStorage: Attempting to get from global storage...');
      console.log('ProductionStorage: Key:', key);
      console.log('ProductionStorage: Global key:', this.GLOBAL_KEY);
      
      if (typeof global !== 'undefined' && (global as any)[this.GLOBAL_KEY]) {
        console.log('ProductionStorage: Found in global:', (global as any)[this.GLOBAL_KEY]);
        return (global as any)[this.GLOBAL_KEY][key] || null;
      } else if (typeof window !== 'undefined' && (window as any)[this.GLOBAL_KEY]) {
        console.log('ProductionStorage: Found in window:', (window as any)[this.GLOBAL_KEY]);
        return (window as any)[this.GLOBAL_KEY][key] || null;
      } else if (typeof globalThis !== 'undefined' && (globalThis as any)[this.GLOBAL_KEY]) {
        console.log('ProductionStorage: Found in globalThis:', (globalThis as any)[this.GLOBAL_KEY]);
        return (globalThis as any)[this.GLOBAL_KEY][key] || null;
      }
      
      console.log('ProductionStorage: No global storage found');
    } catch (error) {
      console.warn('ProductionStorage: Global get failed:', error);
    }
    return null;
  }

  // Persist to global variable
  private static persistToGlobal(): void {
    try {
      console.log('ProductionStorage: Persisting to global storage...');
      console.log('ProductionStorage: Data to persist:', this.data);
      
      if (typeof global !== 'undefined') {
        (global as any)[this.GLOBAL_KEY] = { ...this.data };
        console.log('ProductionStorage: Persisted to global:', (global as any)[this.GLOBAL_KEY]);
      } else if (typeof window !== 'undefined') {
        (window as any)[this.GLOBAL_KEY] = { ...this.data };
        console.log('ProductionStorage: Persisted to window:', (window as any)[this.GLOBAL_KEY]);
      } else if (typeof globalThis !== 'undefined') {
        (globalThis as any)[this.GLOBAL_KEY] = { ...this.data };
        console.log('ProductionStorage: Persisted to globalThis:', (globalThis as any)[this.GLOBAL_KEY]);
      }
    } catch (error) {
      console.warn('ProductionStorage: Global persist failed:', error);
    }
  }

  // Remove item
  static async removeItem(key: string): Promise<void> {
    await this.initialize();
    
    const fullKey = `${this.PREFIX}${key}`;
    
    try {
      // Remove from memory
      delete this.data[fullKey];
      
      // Strategy 1: Try localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.removeItem(fullKey);
        } catch (error) {
          console.warn('ProductionStorage: localStorage remove failed:', error);
        }
      }
      
      // Update global variable
      this.persistToGlobal();
      
    } catch (error) {
      console.error(`ProductionStorage: Error removing ${fullKey}:`, error);
    }
  }

  // Remove multiple items
  static async multiRemove(keys: string[]): Promise<void> {
    await this.initialize();
    
    const fullKeys = keys.map(key => `${this.PREFIX}${key}`);
    
    try {
      // Remove from memory
      fullKeys.forEach(key => delete this.data[key]);
      
      // Strategy 1: Try localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          fullKeys.forEach(key => localStorage.removeItem(key));
        } catch (error) {
          console.warn('ProductionStorage: localStorage multiRemove failed:', error);
        }
      }
      
      // Update global variable
      this.persistToGlobal();
      
    } catch (error) {
      console.error('ProductionStorage: Error removing multiple items:', error);
    }
  }

  /** Wipe cached user profile from memory + localStorage (all known key variants). */
  static async clearUserProfileKeys(): Promise<void> {
    await this.initialize();
    const keys = ['user', 'bookapt_user'];
    await this.multiRemove(keys);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.removeItem('bookapt_bookapt_user');
      } catch {
        /* ignore */
      }
    }
    delete this.data[`${this.PREFIX}user`];
    delete this.data[`${this.PREFIX}bookapt_user`];
    delete this.data['bookapt_bookapt_user'];
    this.persistToGlobal();
  }

  /** Wipe customer + business session caches (use on login/logout account switch). */
  static async clearAuthSessionCaches(): Promise<void> {
    await this.clearUserProfileKeys();
    await this.removeItem('business_owner');
    delete this.data[`${this.PREFIX}business_owner`];
    this.persistToGlobal();
  }

  // Clear all data
  static async clear(): Promise<void> {
    await this.initialize();
    
    try {
      // Clear memory
      this.data = {};
      
      // Strategy 1: Try localStorage
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          const keys = Object.keys(localStorage);
          const bookaptKeys = keys.filter(key => key.startsWith(this.PREFIX));
          bookaptKeys.forEach(key => localStorage.removeItem(key));
        } catch (error) {
          console.warn('ProductionStorage: localStorage clear failed:', error);
        }
      }
      
      // Clear global variable
      this.persistToGlobal();
      
    } catch (error) {
      console.error('ProductionStorage: Error clearing data:', error);
    }
  }

  // Synchronous getter for UI components
  static getItemSync(key: string): string | null {
    const fullKey = `${this.PREFIX}${key}`;
    return this.data[fullKey] || null;
  }

  // Get storage info
  static async getStorageInfo(): Promise<{
    strategy: string;
    itemCount: number;
    isPersistent: boolean;
  }> {
    await this.initialize();
    
    let strategy = 'memory';
    let isPersistent = false;
    
    if (typeof window !== 'undefined' && window.localStorage) {
      strategy = 'localStorage';
      isPersistent = true;
    } else {
      strategy = 'global memory';
      isPersistent = true; // Global memory persists across app restarts
    }
    
    return {
      strategy,
      itemCount: Object.keys(this.data).length,
      isPersistent
    };
  }

  // Debug method to see what's stored
  static async debugStorage(): Promise<void> {
    await this.initialize();
    console.log('ProductionStorage: Current data:', this.data);
    console.log('ProductionStorage: Global storage:', (global as any)?.[this.GLOBAL_KEY]);
    console.log('ProductionStorage: Window storage:', (window as any)?.[this.GLOBAL_KEY]);
    console.log('ProductionStorage: GlobalThis storage:', (globalThis as any)?.[this.GLOBAL_KEY]);
  }
}

export default ProductionStorage;